/**
 * Navegación flotante (diseño propio de HabitOS).
 * Móvil: píldora flotante sobre el contenido; la pestaña activa muestra su
 * etiqueta y un indicador se desliza hasta ella. Escritorio: barra lateral.
 * Accesible: enlaces reales con aria-current y etiquetas siempre presentes
 * para lectores de pantalla.
 */
import { useLayoutEffect, useRef, useState, type ComponentType } from 'react';
import { NavLink, useLocation } from 'react-router';
import { LogoMark } from './Logo';

export interface FloatingNavItem {
  to: string;
  label: string;
  Icon: ComponentType<{ size?: number; 'aria-hidden'?: boolean | 'true' }>;
  end?: boolean;
  featured?: boolean;
}

function isActive(pathname: string, item: FloatingNavItem): boolean {
  return item.end ? pathname === item.to : pathname === item.to || pathname.startsWith(`${item.to}/`);
}

export function FloatingNavigation({ items, label, brand }: { items: FloatingNavItem[]; label: string; brand: string }) {
  const { pathname } = useLocation();
  const listRef = useRef<HTMLUListElement>(null);
  const [indicator, setIndicator] = useState<{ x: number; w: number } | null>(null);
  const activeIndex = items.findIndex((i) => isActive(pathname, i));

  useLayoutEffect(() => {
    const update = () => {
      const list = listRef.current;
      const el = list?.querySelectorAll<HTMLElement>('.fnav__link')[activeIndex];
      if (!list || !el) return setIndicator(null);
      const lr = list.getBoundingClientRect();
      const r = el.getBoundingClientRect();
      setIndicator({ x: r.left - lr.left, w: r.width });
    };
    update();
    // La etiqueta activa cambia el ancho: se vuelve a medir tras la transición.
    const t = window.setTimeout(update, 220);
    window.addEventListener('resize', update);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener('resize', update);
    };
  }, [activeIndex, items.length]);

  return (
    <nav className="fnav" aria-label={label}>
      <div className="fnav__brand">
        <LogoMark size={30} />
        {brand}
      </div>
      <ul className="fnav__list" ref={listRef}>
        {indicator && (
          <li
            className="fnav__indicator"
            aria-hidden="true"
            style={{ transform: `translateX(${indicator.x}px)`, width: indicator.w }}
          />
        )}
        {items.map((item) => {
          const active = isActive(pathname, item);
          return (
            <li key={item.to}>
              <NavLink
                to={item.to}
                end={item.end}
                className={item.featured ? 'fnav__link fnav__link--featured' : 'fnav__link'}
                data-active={active ? 'true' : undefined}
                data-featured={item.featured ? 'true' : undefined}
              >
                <item.Icon size={22} aria-hidden="true" />
                <span className="fnav__label">{item.label}</span>
              </NavLink>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
